import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Trash2, Save, Truck, Bitcoin } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const emptyZone = { country_code: '', country_name: '', price: 0, free_over: 0, eta: '2-5 working days', enabled: true };
const emptyProvider = { id: '', name: '', url: '', description: '', logo_url: '', enabled: true };
const localKey = () => `loc_${Math.random().toString(36).slice(2, 10)}`;
const withKey = (arr) => (arr || []).map((x) => ({ _key: x._key || localKey(), ...x }));

const AdminShipping = () => {
  const [data, setData] = useState({ zones: [], voucher_providers: [], default_price: 15, voucher_instructions: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.getShippingSettings().then((d) => setData({
    zones: withKey(d.zones), voucher_providers: withKey(d.voucher_providers),
    default_price: d.default_price ?? 15, voucher_instructions: d.voucher_instructions || '',
  })).catch((err) => console.error('shipping load failed:', err)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const updateZone = (idx, key, val) => {
    const z = [...data.zones]; z[idx] = { ...z[idx], [key]: val }; setData({ ...data, zones: z });
  };
  const addZone = () => setData({ ...data, zones: [...data.zones, { ...emptyZone, _key: localKey() }] });
  const delZone = (idx) => setData({ ...data, zones: data.zones.filter((_, i) => i !== idx) });

  const updateProv = (idx, key, val) => {
    const p = [...data.voucher_providers]; p[idx] = { ...p[idx], [key]: val }; setData({ ...data, voucher_providers: p });
  };
  const addProv = () => setData({ ...data, voucher_providers: [...data.voucher_providers, { ...emptyProvider, _key: localKey() }] });
  const delProv = (idx) => setData({ ...data, voucher_providers: data.voucher_providers.filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      // Normalize numbers and strip local-only _key field
      const stripKey = ({ _key, ...rest }) => rest;
      const payload = {
        ...data,
        default_price: Number(data.default_price) || 0,
        zones: data.zones.map((z) => ({ ...stripKey(z), price: Number(z.price) || 0, free_over: Number(z.free_over) || 0, country_code: (z.country_code || '').toUpperCase() })),
        voucher_providers: data.voucher_providers.map((p) => ({ ...stripKey(p), id: (p.id || '').toLowerCase().replace(/[^a-z0-9_-]/g, '') })),
      };
      await api.putShippingSettings(payload);
      toast.success('Shipping settings saved');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="admin-shipping-page">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Manage fixed shipping prices per country + the crypto voucher providers customers are sent to when paying with fiat.</p>
        <Button onClick={save} disabled={saving} className="btn-gold" data-testid="shipping-save"><Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save all'}</Button>
      </div>

      {/* Shipping zones */}
      <Card className="bg-zinc-950 border-zinc-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="serif-italic text-2xl text-[#d4af37] flex items-center gap-2"><Truck className="h-5 w-5" /> Shipping zones (per country)</h2>
          <Button onClick={addZone} variant="outline" className="btn-outline-gold h-9" data-testid="shipping-zone-add"><Plus className="h-4 w-4 mr-2" /> Add country</Button>
        </div>

        <div className="mb-4 flex items-end gap-4">
          <div className="max-w-xs">
            <Label className="text-zinc-300">Fallback price (countries not listed)</Label>
            <Input type="number" step="0.01" value={data.default_price} onChange={(e) => setData({ ...data, default_price: e.target.value })} className="bg-transparent border-zinc-800 mt-1" />
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-zinc-400 text-left">
              <tr>
                <th className="p-2">Code</th><th className="p-2">Country</th><th className="p-2">Price (€)</th><th className="p-2">Free over (€)</th><th className="p-2">ETA</th><th className="p-2">Active</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.zones.map((z, idx) => (
                <tr key={z._key} className="border-t border-zinc-900">
                  <td className="p-2"><Input value={z.country_code} onChange={(e) => updateZone(idx, 'country_code', e.target.value.toUpperCase().slice(0, 2))} className="bg-transparent border-zinc-800 h-9 w-16 uppercase" /></td>
                  <td className="p-2"><Input value={z.country_name} onChange={(e) => updateZone(idx, 'country_name', e.target.value)} className="bg-transparent border-zinc-800 h-9" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={z.price} onChange={(e) => updateZone(idx, 'price', e.target.value)} className="bg-transparent border-zinc-800 h-9 w-24" /></td>
                  <td className="p-2"><Input type="number" step="0.01" value={z.free_over} onChange={(e) => updateZone(idx, 'free_over', e.target.value)} className="bg-transparent border-zinc-800 h-9 w-24" /></td>
                  <td className="p-2"><Input value={z.eta} onChange={(e) => updateZone(idx, 'eta', e.target.value)} className="bg-transparent border-zinc-800 h-9" /></td>
                  <td className="p-2"><Switch checked={!!z.enabled} onCheckedChange={(v) => updateZone(idx, 'enabled', v)} /></td>
                  <td className="p-2 text-right"><Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => delZone(idx)}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
              {data.zones.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-zinc-500">No zones — click &quot;Add country&quot; to define one.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Voucher providers */}
      <Card className="bg-zinc-950 border-zinc-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="serif-italic text-2xl text-[#d4af37] flex items-center gap-2"><Bitcoin className="h-5 w-5" /> Crypto voucher providers</h2>
          <Button onClick={addProv} variant="outline" className="btn-outline-gold h-9" data-testid="voucher-provider-add"><Plus className="h-4 w-4 mr-2" /> Add provider</Button>
        </div>
        <p className="text-zinc-500 text-sm mb-4">These appear on the post-checkout instruction page for customers who choose fiat.</p>

        <div className="space-y-3">
          {data.voucher_providers.map((p, idx) => (
            <div key={p._key} className="grid md:grid-cols-12 gap-3 border border-zinc-900 rounded p-3 items-start">
              <div className="md:col-span-2"><Label className="text-zinc-300 text-xs">ID (slug)</Label><Input value={p.id} onChange={(e) => updateProv(idx, 'id', e.target.value)} className="bg-transparent border-zinc-800 h-9 mt-1 font-mono text-sm" /></div>
              <div className="md:col-span-3"><Label className="text-zinc-300 text-xs">Name</Label><Input value={p.name} onChange={(e) => updateProv(idx, 'name', e.target.value)} className="bg-transparent border-zinc-800 h-9 mt-1" /></div>
              <div className="md:col-span-3"><Label className="text-zinc-300 text-xs">URL</Label><Input value={p.url} onChange={(e) => updateProv(idx, 'url', e.target.value)} className="bg-transparent border-zinc-800 h-9 mt-1" placeholder="https://…" /></div>
              <div className="md:col-span-3"><Label className="text-zinc-300 text-xs">Description</Label><Input value={p.description} onChange={(e) => updateProv(idx, 'description', e.target.value)} className="bg-transparent border-zinc-800 h-9 mt-1" /></div>
              <div className="md:col-span-1 flex items-center justify-end gap-2 pt-5"><Switch checked={!!p.enabled} onCheckedChange={(v) => updateProv(idx, 'enabled', v)} /><Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => delProv(idx)}><Trash2 className="h-4 w-4" /></Button></div>
            </div>
          ))}
          {data.voucher_providers.length === 0 && <p className="text-zinc-500 text-sm">No providers — add at least one (e.g. Bitrefill).</p>}
        </div>

        <div className="mt-5">
          <Label className="text-zinc-300">Voucher instructions (shown to customer)</Label>
          <Textarea rows={3} value={data.voucher_instructions} onChange={(e) => setData({ ...data, voucher_instructions: e.target.value })} className="bg-transparent border-zinc-800 mt-1" />
        </div>
      </Card>
    </div>
  );
};

export default AdminShipping;
